import styles from './onboarding.module.css';
import React, { useState, useContext } from 'react';
import { CurrentMetadataForUser } from "../../context/CurrentMetadataForUser";
import { AppBGColors, GreetingColors, AppColorTheme } from '../../Colors';
import PrevMobile from '../PrevMobile';
import { createOrUpdateProfile } from '../../adapters/profile/profile-adapter';
import { getScheduleExpForNextBizDay } from '../../helpers/RemindersHelper';
import { createReminderSchedule } from '../../adapters/reminders-adapter';
import { isPhoneNumberValid } from '../PhoneNumberInput';
import { TimezoneMap } from '../../Constants';
import NameInput from './NameInput';
import Intro from './Intro';
import TimeRequirement from './TimeRequirement';
import Outcome from './Outcome';
import FinalScreen from './FinalScreen';
import Rhythm from './Rhythm';
import TextReminderSetup from './TextReminderSetup';
import HowItWorks from './HowItWorks';
import WorkInfo from './WorkInfo';
import InsightsPreview from './InsightsPreview';
import YourWhy from './YourWhy';

function Onboarding(props) {
    const backgroundColor = AppBGColors[props.timeOfDay];
    const windowColor = GreetingColors[props.timeOfDay].background;
    const buttonTextColor = AppColorTheme[props.timeOfDay].DARK;

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone; // Client side timezone
    const [currentStep, setCurrentStep] = useState(0);
    const [name, setName] = useState('');
    const [company, setCompany] = useState('');
    const [role, setRole] = useState('');
    const [onWorkBreak, setOnWorkBreak] = useState(false);
    const [whyReason, setWhyReason] = useState('');
    const [eventTime, setEventTime] = useState("12:00");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [isProfileSaved, setProfileSaveStatus] = useState(false);
    const [isReminderScheduleCreated, setScheduleCreationStatus] = useState(false);
    const [phoneNumberSaveError, setPhoneNumberSaveError] = useState(false);
    const { setCurrentMetadataForUser } = useContext(CurrentMetadataForUser);

    const nextStep = () => {
        if (currentStep < components.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const prevStep = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const validatePhoneNumber = (phoneNumber) => {
        if (phoneNumber.length !== 10 || !isPhoneNumberValid(phoneNumber)) {
            setPhoneNumberSaveError(true);
            return false;
        }
        setPhoneNumberSaveError(false);
        return true;
    }

    const components = [
        {
            component: <NameInput name={name} setName={setName} />,
            buttonText: 'BEGIN ONBOARDING',
            key: 'name-input',
        },
        {
            component: <WorkInfo name={name} company={company} setCompany={setCompany}
                role={role} setRole={setRole} onWorkBreak={onWorkBreak} setOnWorkBreak={setOnWorkBreak} />,
            buttonText: 'NEXT',
            key: 'company-and-role-info',
        },
        { component: <Intro name={name} />, buttonText: 'HOW DOES IT WORK?', key: 'intro' },
        { component: <HowItWorks name={name} />, buttonText: 'HOW LONG DOES IT TAKE?', key: 'how-it-works' },
        { component: <TimeRequirement />, buttonText: 'WHAT HAPPENS THEN?', key: 'time-requirement' },
        { component: <Outcome />, buttonText: `GIMME A PREVIEW`, key: 'outcome' },
        { component: <InsightsPreview />, buttonText: 'WHAT IF I MISS A DAY..?', key: 'insights-preview' },
        { component: <Rhythm name={name} />, buttonText: 'HELP ME REMEMBER', key: 'rhythm' },
        { component: <YourWhy whyReason={whyReason} setWhyReason={setWhyReason} />, buttonText: 'LOCK IT IN', key: 'your-why' },
        {
            component: <TextReminderSetup phoneNumber={phoneNumber} setPhoneNumber={setPhoneNumber}
                phoneNumberError={phoneNumberSaveError} setPhoneNumberError={setPhoneNumberSaveError}
                eventTime={eventTime} setEventTime={setEventTime} />,
            buttonText: 'GET A REMINDER',
            key: 'text-reminder',
            onClick: () => {
                if (validatePhoneNumber(phoneNumber)) {
                    nextStep();
                }
            },
            secondaryOption: `I don't need support`,
            secondaryOnClick: () => {
                if (phoneNumber?.length > 0 && !isPhoneNumberValid(phoneNumber)) {
                    // unset invalid phone number so it doesn't get saved
                    setPhoneNumber('');
                    setPhoneNumberSaveError(false)
                }
                nextStep();
            }
        },
        {
            component: <FinalScreen wasPhoneNumProvided={phoneNumber?.length > 1} />,
            buttonText: 'LET\'S FLOW',
            key: 'final-screen',
            onClick: () => {
                finishOnboarding();
            }
        }
    ];

    const finishOnboarding = () => {
        if (!isProfileSaved) {
            saveProfile();
        }
        if (phoneNumber && phoneNumber.length > 0 && !isReminderScheduleCreated) {
            scheduleRemindersForUser();
        }
        props.finishOnboarding();
    }

    const saveProfile = () => {
        const profile = {
            userId: props.userId,
            type: 'profile',
            name: name,
            companyName: company || (onWorkBreak && 'On work break'),
            role: role || (onWorkBreak && 'On work break'),
            whyReason: whyReason,
            phone: phoneNumber,
            timezone: TimezoneMap[timezone], // In profile we use abbreviations (PST, EST, etc.)
            eventTime: eventTime,
        };

        //Make API call to save profile
        createOrUpdateProfile(profile).then(async response => {
            if (response.ok) {
                setProfileSaveStatus(true);
            }
            else {
                // TODO: Show error message on UI
                const message = await response.text();
                console.error(message);
            }
        });
    }

    const scheduleRemindersForUser = () => {
        if (!phoneNumber || !isPhoneNumberValid(phoneNumber)) {
            setCurrentMetadataForUser({ remindersOn: false });
            return;
        }

        const scheduleExpression = getScheduleExpForNextBizDay(new Date(), eventTime);
        const schedule = {
            "Name": `rule${props.userId}`,
            "Description": `For ${name}`,
            "ScheduleExpression": scheduleExpression,
            "ScheduleExpressionTimezone": timezone,
            "State": 'ENABLED',
            "Input": {
                "userId": props.userId,
                "phone": `+1${phoneNumber}`,
                "message": `Hi ${name}, Feel to find balance 🌻`,
                "reminderIndex": -1
            },
            "FlexibleTimeWindow": {
                "Mode": "OFF"
            }
        }
        createReminderSchedule(schedule).then(async response => {
            if (response.ok) {
                // const message = await response.text();
                // console.log(message);
                setScheduleCreationStatus(true);
                setCurrentMetadataForUser({ remindersOn: true });
            }
        });
    }

    const backButtonStyles = {
        color: "#ffffffeb",
        borderColor: "#ffffff6e",
        margin: 0,
        fontSize: "1rem",
        minWidth: "unset",
        padding: "0.1rem 0.5rem",
    };

    const isNextEnabled = () => {
        if (currentStep === 0) {
            return name && name.trim().length > 0;
        }
        if (currentStep === 1) {
            return (company && company.trim().length > 0 && role && role.trim().length > 0) || onWorkBreak;
        }
        if (currentStep === 8) {
            const words = whyReason.trim().split(/\s+/);
            return words.length >= 5;
        }
        if (currentStep === 9) {
            return phoneNumber.length === 10;
        }

        return true;
    }

    return (
        <div className={styles.mainContent} style={{ background: backgroundColor, flexDirection: "column" }}>
            <div className={styles.onboardingWindow} style={{ background: windowColor }}>
                {currentStep > 0 &&
                    <div className={styles.backBtnDiv}>
                        <PrevMobile visible={true} onClick={prevStep} customStyles={backButtonStyles} />
                    </div>
                }
                {components[currentStep].component}
                <button className={styles.onboardingBtn} style={{ color: buttonTextColor }}
                    onClick={() => (components[currentStep].onClick ? components[currentStep].onClick() : nextStep())}
                    disabled={isNextEnabled() ? false : true}
                >
                    {components[currentStep].buttonText}
                </button>
                {components[currentStep].secondaryOption &&
                    <p className={styles.secondaryOption} onClick={components[currentStep].secondaryOnClick}>
                        {components[currentStep].secondaryOption}
                    </p>
                }
            </div>
        </div>
    )
}

export default Onboarding;